import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Briefcase, Code2, CheckCircle2 } from 'lucide-react';

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  enter: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 }
};

const staggerContainer = {
  enter: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  enter: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

function HomePage() {
  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="enter"
      exit="exit"
      transition={{ duration: 0.4 }}
    >
      <HeroSection />
      <ServicesPreview />
    </motion.div>
  );
}

function HeroSection() {
  return (
    <section className="hero">
      <div className="hero__background">
        <div className="hero__grid" />
        <div className="hero__glow hero__glow--primary" />
        <div className="hero__glow hero__glow--secondary" />
      </div>

      <div className="container">
        <motion.div
          className="hero__content"
          variants={staggerContainer}
          initial="initial"
          animate="enter"
        >
          <motion.div variants={fadeInUp} className="hero__badge">
            <span className="hero__badge-dot" />
            IT-konsulent & Softwareudvikling
          </motion.div>

        <motion.h1 variants={fadeInUp} className="hero__title">
  <span className="gradient-text">Core4it</span>
</motion.h1>

<motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl font-bold tracking-tight">
  Fra komplekse systemer til enkle hverdage
</motion.h2>

<motion.p 
  variants={fadeInUp} 
  className="hero__subtitle"
  style={{ marginTop: '40px' }} // seb er dum
>
  Vi transformerer dine mest tidskrævende arbejdsgange til automatiserede processer. 
  Hos Core4it bygger vi fundamentet, der fjerner hverdagens besvær, så du kan 
  fokusere på det, der skaber reel værdi.
</motion.p>

          <motion.div variants={fadeInUp} className="hero__actions">
            <Link to="/contact" className="btn btn--primary btn--large">
              Book en samtale
              <ArrowRight className="btn__icon" />
            </Link>
            <Link to="/services" className="btn btn--secondary btn--large">
              Hør mere
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

function ServicesPreview() {
  const services = [
    {
      icon: Briefcase,
      title: 'Workflow Automatisering (Service)',
      description:
        'Bruger I for meget tid på manuelle opgaver? Vi hjælper jer med at automatisere de arbejdsgange, der stjæler tid i hverdagen. Det sparer jer penge, reducerer fejl og giver mere tid til det, der virkelig tæller.',
      features: [
        'Automatisering af kundehenvendelser',
        'Automatisering af fakturering og tilbud',
        'Automatisering af lagerstyring og ordre', 
        'Skræddersyede løsninger til jeres behov'
        
      ],
      color: 'blue'
    },
    {
      icon: Code2,
      title: 'Softwareudvikling (Egne produkter)',
      description:
        'Hos Core4it nøjes vi ikke med at rådgive – vi bygger. Ved siden af vores automatiseringer udvikler og driver vi vores egne software-løsninger, der gør kompleks teknologi tilgængelig for alle. Under udvikling findes:',
      features: [
        'Noder - Excel for kodning - coming soon',
        'Sportslink - Sportens mødested - coming soon',
        'Og mere på vej...'
      ],
      color: 'cyan'
    }
  ];

  return (
    <section className="section">
      <div className="container">
        <motion.div
          className="section__header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <span className="section__tag">Vores Services</span>
          <h2 className="section__title">Hvad vi tilbyder</h2>
          <p className="section__description">
            Vi bygger software til dine behov og intelligente agenter til dine processer. Core4it fjerner spildtid med automatisering, der tænker selv, så du får friheden tilbage til at skabe reel værdi.
          </p>
        </motion.div>

        <div className="services-grid">
          {services.map((service, index) => (
            <motion.article
              key={service.title}
              className="service-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <div className={`service-card__glow service-card__glow--${service.color}`} />
              
              <div className={`service-card__icon service-card__icon--${service.color}`}>
                <service.icon />
              </div>

              <h3 className="service-card__title">{service.title}</h3>
              <p className="service-card__text">{service.description}</p>

              <ul className="service-card__list">
                {service.features.map((feature) => (
                  <li key={feature} className="service-card__item">
                    <CheckCircle2 className="service-card__check" />
                    {feature}
                  </li>
                ))}
              </ul>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}

export default HomePage;
